import { useEffect, useState } from "react";
import { productCategoryService } from "../../../Api/Services/productCategoryService";
import { ProductCategory } from "../../../Models/ProductCategory";
import { Input } from "../../../Components/Input/Input";
import { FaBoxOpen, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import FloatingInput from "../../../Components/FloatingInput/FloatingInput";
import BackofficePage from "../../../Components/BackofficePage/BackofficePage";
import Button from "../../../Components/Button/Button";
import BlockedAction from "../../../Components/BlockedAction/BlockedAction";

const DELETE_BLOCKED_REASON = "Esta categoria não pode ser excluída porque está vinculada a produtos.";

const AdminProductCategoryPage = () => {

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [showCategoryForm, setShowCategoryForm] = useState<boolean>(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setIsLoading(true);
      const data = await productCategoryService.getAll();
      setCategories(data);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setNewName("");
    setNewDescription("");
    setShowCategoryForm(false);
  };

  const hasDescription = (description?: string | null) => !!description?.trim();

  const getDescriptionLabel = (description?: string | null) => {
    if (!hasDescription(description)) {
      return "Sem descrição";
    }

    return "Com descrição";
  };

  const handleSave = async () => {
    if (!newName.trim()) {
      return;
    }

    const payload = {
      name: newName,
      description: newDescription.trim() || null
    };

    if (editId) {
      await productCategoryService.update({ id: editId, ...payload });
      toast.success("Categoria atualizada com sucesso!");
    } else {
      await productCategoryService.create(payload);
      toast.success("Categoria adicionada com sucesso!");
    }

    resetForm();
    load();
  };

  const handleDelete = async (id: string) => {
    try {
      const deleted = await productCategoryService.delete(id);

      if (deleted) {
        toast.success("Categoria removida com sucesso!");
        load();
      }
    } catch (err: any) {
      toast.error(
        err.response?.data ||
          "Não foi possível remover a categoria, tente novamente mais tarde."
      );
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SmartNavbar />
      <BackofficePage
        title={"CATEGORIAS DE PRODUTO"}
        className={categories.length > 0 ? "max-w-5xl" : "w-full"}
        fullHeight
      >
        {showCategoryForm ? (
          <div className="mb-4 flex flex-col gap-3 w-full">
            <FloatingInput
              label={"Nome da categoria"}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <Input
              label={"Texto da categoria"}
              type="textarea"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              rows={5}
              placeholder={"Digite um texto opcional para aparecer na página pública da categoria."}
            />

            <div className="flex gap-2">
              <Button variant="secondary"
                onClick={resetForm}
                className="w-full sm:w-32 px-4 py-2"
              >
                {"CANCELAR"}
              </Button>

              <Button
                onClick={handleSave}
                className="w-full sm:w-32 px-4 py-2"
              >
                {editId ? "SALVAR" : "ADICIONAR"}
              </Button>
            </div>
          </div>
        ) : (
          <button
            className="uppercase sm:normal-case w-full sm:w-auto flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal my-2 hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700"
            onClick={() => setShowCategoryForm(true)}
          >
            <FaPlus /> {"Adicionar categoria"}
          </button>
        )}

        {isLoading ? (
          <div className="mt-6 space-y-4 w-full">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white border rounded p-4 shadow-sm">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
            <FaBoxOpen className="text-3xl" />
            <p className="text-center text-sm sm:text-base">{"Nenhuma categoria encontrada"}</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 sm:hidden">
              {categories.map((category, index) => (
                <div key={category.id} className={`border rounded p-4 shadow-sm bg-white`}>
                  <h2 className="font-semibold mb-2">
                    {"Categoria"} {index + 1}
                  </h2>
                  <p>
                    <span className="font-medium">{"Nome"}:</span> {category.name}
                  </p>
                  <p>
                    <span className="font-medium">{"Descrição"}:</span>{" "}
                    {hasDescription(category.description) ? category.description : getDescriptionLabel(category.description)}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm"
                      onClick={() => {
                        setEditId(category.id);
                        setNewName(category.name);
                        setNewDescription(category.description ?? "");
                        setShowCategoryForm(true);
                      }}
                    >
                      <FaEdit /> {"Editar"}
                    </Button>
                    {category.canDelete === false ? (
                      <BlockedAction
                        label={"Excluir"}
                        reason={DELETE_BLOCKED_REASON}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm"
                      >
                        <FaTrash /> {"Excluir"}
                      </BlockedAction>
                    ) : (
                      <Button
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm"
                        onClick={() => handleDelete(category.id)}
                      >
                        <FaTrash /> {"Excluir"}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="w-full max-w-5xl">
              <table className={`hidden sm:table table-fixed w-full border text-left mb-20`}>
                <thead>
                  <tr className="bg-primary-120">
                    <th className="p-2 w-2/5">{"Nome"}</th>
                    <th className="p-2 w-2/5">{"Descrição"}</th>
                    <th className="p-2 w-1/5 text-right">{"Ações"}</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat.id} className="border-t">
                      <td className="p-2 truncate">{cat.name}</td>
                      <td className="p-2">
                        <span
                          className={`block truncate ${hasDescription(cat.description) ? "text-gray-800" : "text-gray-500"}`}
                          title={cat.description ?? undefined}
                        >
                          {hasDescription(cat.description) ? `${"Com descrição"}: ${cat.description}` : getDescriptionLabel(cat.description)}
                        </span>
                      </td>
                      <td className="p-2">
                        <div className="flex justify-end items-center gap-3 whitespace-nowrap">
                          <button
                            className="text-primary-600"
                            onClick={() => {
                              setEditId(cat.id);
                              setNewName(cat.name);
                              setNewDescription(cat.description ?? "");
                              setShowCategoryForm(true);
                            }}
                          >
                            <FaEdit />
                          </button>
                          {cat.canDelete === false ? (
                            <BlockedAction label={"Excluir"} reason={DELETE_BLOCKED_REASON} className="text-gray-300" plain>
                              <FaTrash />
                            </BlockedAction>
                          ) : (
                            <button className="text-primary-600" onClick={() => handleDelete(cat.id)}>
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </BackofficePage>
    </div>
  );
};

export default AdminProductCategoryPage;
